/**
 * FranchiseApplicationsPage — `/franchise/applications`.
 *
 * Backed by the dedicated `GET /v1/franchise/franchises/applications`
 * endpoint — joins each application back to the franchise so the operator can
 * see who applied, at which tier, and the application status (PENDING /
 * UNDER_REVIEW / APPROVED / REJECTED) independently of the franchise
 * lifecycle status.
 */
import { type JSX, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  type ApplicationStatus,
  type FranchiseApplicationListItem,
  listAllApplications,
} from '../../lib/api/franchise';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { FilterBar } from '../mail/shared/MailUI';

const APP_STATUSES: readonly ApplicationStatus[] = [
  'PENDING',
  'UNDER_REVIEW',
  'APPROVED',
  'REJECTED',
];

export function FranchiseApplicationsPage(): JSX.Element {
  const [status, setStatus] = useState<ApplicationStatus | ''>('');
  const [search, setSearch] = useState('');

  const list = useQuery({
    queryKey: ['franchise', 'applications', 'list', status],
    queryFn: () =>
      listAllApplications({
        pageSize: 200,
        ...(status === '' ? {} : { status: status as ApplicationStatus }),
      }),
    retry: false,
  });

  const denied = list.isError && list.error instanceof ApiError &&
    (list.error.status === 401 || list.error.status === 403);

  const items: readonly FranchiseApplicationListItem[] = useMemo(() => {
    const rows = list.data?.items ?? [];
    if (search.trim().length === 0) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) => {
      const f = r.franchise;
      return (
        (f?.legalName ?? '').toLowerCase().includes(q) ||
        (f?.franchiseNumber ?? '').toLowerCase().includes(q) ||
        (f?.state ?? '').toLowerCase().includes(q) ||
        r.id.toLowerCase().includes(q)
      );
    });
  }, [list.data, search]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Franchise applications"
        description="Application records — PENDING / UNDER_REVIEW / APPROVED / REJECTED."
        actions={
          <Link
            to="/franchise/applications/new"
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> New application
          </Link>
        }
      />

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Filter by name, number, state or app id…"
      >
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as ApplicationStatus | '')}
          className="px-3 py-2 border border-border rounded-lg text-sm"
        >
          <option value="">All applications</option>
          {APP_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </FilterBar>

      {list.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          The dev token doesn&apos;t carry <code className="font-mono">FranchiseApplication/read</code>.
        </DeferredNotice>
      ) : list.isError ? (
        <DeferredNotice title="Service unreachable">{(list.error as Error).message}</DeferredNotice>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          No applications match this filter.
        </p>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left px-4 py-2">App id</th>
                <th className="text-left px-4 py-2">Number</th>
                <th className="text-left px-4 py-2">Legal name</th>
                <th className="text-left px-4 py-2">Tier</th>
                <th className="text-left px-4 py-2">State / LGA</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Submitted</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50" data-testid="application-row">
                  <td className="px-4 py-2 font-mono text-[11px]">{row.id.slice(0, 8)}</td>
                  <td className="px-4 py-2 font-mono text-xs">{row.franchise?.franchiseNumber ?? '—'}</td>
                  <td className="px-4 py-2 text-xs text-gray-700">{row.franchise?.legalName ?? '—'}</td>
                  <td className="px-4 py-2 text-xs text-gray-700">{row.requestedTier}</td>
                  <td className="px-4 py-2 text-xs text-gray-700">
                    {row.franchise ? `${row.franchise.state} / ${row.franchise.lga}` : '—'}
                  </td>
                  <td className="px-4 py-2"><StatusBadge status={row.status} /></td>
                  <td className="px-4 py-2 text-xs text-muted">
                    {new Date(row.submittedAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {row.franchise ? (
                      <Link
                        to={`/franchise/applications/${encodeURIComponent(row.franchise.id)}/onboard`}
                        className="text-xs font-semibold text-nipost-green hover:underline"
                      >
                        Onboard →
                      </Link>
                    ) : (
                      <span className="text-xs text-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
